/**
 * Shared types + display constants for the unified Action Items feed.
 *
 * Pre-migration-0081 this file housed ~250 lines of regex-based dedup
 * (extractProviderFromTitle, buildInsightKeys, isTaskDuplicate, etc.)
 * because the page merged three separate API responses client-side.
 * That's gone — the server now returns one unified, pre-deduped list
 * from /api/action-items. This file is now just shapes and styling.
 */

// ============================================================================
// UnifiedItem — matches the ActionItem schema returned by /api/action-items
// ============================================================================

/**
 * `kind` discriminates row origin (Phase 1 PR 14):
 *   insight / alert / task — persistent rows in `action_items`.
 *   standing               — virtual rows computed from `sources` state
 *                            (Unclassified, or Approved-Misconfigured).
 *                            No fingerprint, no status transitions; the
 *                            way to "resolve" one is to classify or fix
 *                            the source.
 */
export type UnifiedKind = "insight" | "alert" | "task" | "standing";

export interface UnifiedItem {
  id: string;
  organizationId: string;
  domainId: string | null;
  sourceId: string | null;
  kind: UnifiedKind;
  category: string;
  subject: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  confidence: "high" | "medium" | "low" | null;
  title: string;
  description: string;
  recommendations: unknown[];
  metadata: Record<string, unknown>;
  /** Internal navigation hint. Null on partner-shared surfaces. */
  internalActionLink: string | null;
  /** Partner-facing copy for shareable categories. Null otherwise. */
  partnerHandoff: string | null;
  /** True when the category is allowed in APEX shares. */
  isPartnerShareable: boolean;
  status: "active" | "snoozed" | "completed" | "dismissed" | "not_applicable";
  snoozedUntil: string | null;
  completedAt: string | null;
  createdAt: string;
  lastSeenAt: string;
  domainName: string | null;
}

// ============================================================================
// Display constants — kept from the previous incarnation since the UI's
// visual vocabulary (severity colors, kind badges) doesn't change.
// ============================================================================

export const PRIORITY_ORDER: Record<UnifiedItem["severity"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

export const BORDER_COLORS: Record<UnifiedItem["severity"], string> = {
  critical: "border-l-red-500",
  high: "border-l-orange-500",
  medium: "border-l-amber-400",
  low: "border-l-blue-400",
  info: "border-l-slate-400",
};

export const SEVERITY_BADGE: Record<UnifiedItem["severity"], string> = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700",
  info: "bg-slate-100 text-slate-600",
};

export const SOURCE_BADGE: Record<UnifiedItem["kind"], { label: string; className: string }> = {
  insight: { label: "AI Insight", className: "bg-purple-100 text-purple-700" },
  task: { label: "Task", className: "bg-blue-100 text-blue-700" },
  alert: { label: "Alert", className: "bg-amber-100 text-amber-700" },
  // Standing-condition rows surface as "Triage" — the user resolves
  // them by classifying or fixing the source, not by snoozing/completing.
  standing: { label: "Triage", className: "bg-rose-100 text-rose-700" },
};

/**
 * Map a category slug to a human-readable label for the type badge on
 * each item. Pulled from the taxonomy labels; unknown categories fall
 * back to a title-cased version of the slug.
 */
export const CATEGORY_LABELS: Record<string, string> = {
  spf_missing_include: "SPF Setup",
  dkim_misconfigured: "DKIM Setup",
  auth_alignment_issue: "Alignment",
  provider_classification_needed: "Is this you?",
  provider_classification_summary: "Unclassified Senders",
  unauthorized_provider_detected: "Unauthorized",
  spf_lookup_budget_exceeded: "SPF Budget",
  spf_permerror: "SPF Error",
  spf_too_many_lookups: "SPF Lookups",
  dmarc_record_missing: "DMARC Missing",
  policy_drift: "Policy Drift",
  enforcement_ready: "Enforcement",
  signing_domain_unaligned: "Signing Domain",
  signing_domain_summary: "Signing Summary",
  dkim_selector_rotated: "DKIM Rotated",
  dkim_selector_stale: "DKIM Stale",
  dkim_selector_missing: "DKIM Missing",
  dkim_selector_abandoned: "DKIM Abandoned",
  new_unauthorized_source: "New Source",
  report_gap: "Report Gap",
  high_failure_rate: "High Failures",
  dns_misconfiguration: "DNS Issue",
};

export function categoryLabel(category: string): string {
  return (
    CATEGORY_LABELS[category] ||
    category
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * Convenience: sort items by severity then recency. The API already does
 * this server-side, but the web sometimes merges items from multiple
 * fetches (e.g. a per-domain page) and needs the canonical order.
 */
export function sortItems(items: UnifiedItem[]): UnifiedItem[] {
  return [...items].sort((a, b) => {
    const sevDelta = PRIORITY_ORDER[a.severity] - PRIORITY_ORDER[b.severity];
    if (sevDelta !== 0) return sevDelta;
    return new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime();
  });
}
